import { roundToCents } from "@/lib/money";
import { firstRecord, getOptionalString, toNumber } from "@/lib/utils/query-helpers";

export interface PaymentSplitMetadata {
  applied_amount: number;
  credit_id: string | null;
  estimate_id?: string | null;
  invoice_id?: string | null;
  payment_id: string | null;
  remainder_amount?: number;
  remainder_credit_amount: number;
  requested_amount: number;
}

interface PaymentSplitDefaults {
  estimateId?: string | null;
  fallbackRemainderCreditAmount?: number;
  invoiceId?: string | null;
  requestedAmount: number;
}

interface PaymentSplitIdentifiers {
  applicationId: string | null;
  creditId: string | null;
  paymentId: string | null;
}

export interface NormalizedPaymentSplitResult {
  ids: PaymentSplitIdentifiers;
  raw: Record<string, unknown> | null;
  split: PaymentSplitMetadata;
}

export function normalizePaymentSplitResult(
  value: unknown,
  defaults: PaymentSplitDefaults,
): NormalizedPaymentSplitResult {
  const raw = firstRecord(value);
  const requestedAmount =
    toNumber(raw?.requested_amount) ?? toNumber(raw?.requestedAmount) ?? defaults.requestedAmount;
  const appliedAmount = toNumber(raw?.applied_amount) ?? toNumber(raw?.appliedAmount) ?? 0;
  // The remainder banked as credit is what the split did not apply, so it has
  // to come off the same `requestedAmount` the split reports. Reading
  // `defaults.requestedAmount` here instead broke that invariant whenever the
  // rpc returned its own requested amount, and the raw subtraction carried
  // float drift into a figure a customer is credited.
  const fallbackRemainderCreditAmount =
    defaults.fallbackRemainderCreditAmount ??
    roundToCents(Math.max(0, requestedAmount - appliedAmount));
  const remainderAmount = toNumber(raw?.remainder_amount) ?? toNumber(raw?.remainderAmount);
  const remainderCreditAmount =
    toNumber(raw?.remainder_credit_amount) ??
    toNumber(raw?.remainderCreditAmount) ??
    fallbackRemainderCreditAmount;

  const ids: PaymentSplitIdentifiers = {
    paymentId: getOptionalString(raw, "payment_id") ?? getOptionalString(raw, "paymentId"),
    creditId: getOptionalString(raw, "credit_id") ?? getOptionalString(raw, "creditId"),
    applicationId:
      getOptionalString(raw, "application_id") ?? getOptionalString(raw, "applicationId"),
  };

  const split: PaymentSplitMetadata = {
    requested_amount: requestedAmount,
    applied_amount: appliedAmount,
    remainder_credit_amount: remainderCreditAmount,
    payment_id: ids.paymentId,
    credit_id: ids.creditId,
  };

  if (defaults.invoiceId !== undefined) {
    split.invoice_id = defaults.invoiceId;
  }
  if (defaults.estimateId !== undefined) {
    split.estimate_id = defaults.estimateId;
  }
  if (remainderAmount !== null) {
    split.remainder_amount = remainderAmount;
  }

  return { raw, ids, split };
}
